import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { toErrorMessage } from '../../../helpers/http-error';
import { AnalyticsService, isUnresolvedScope } from '../../../services/analytics.service';
import { LocaleService } from '../../../services/locale.service';
import {
  PeriodPreset,
  RentalSummary,
  SeriesPoint,
  resolvePeriod,
} from '../../../types/analytics.types';
import { TPipe } from '../../../pipe/t.pipe';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../ui/pretty-select/pretty-select.component';
import { AnalyticsDonutComponent } from '../analytics-donut/analytics-donut.component';
import { AnalyticsSeriesChartComponent } from '../analytics-series-chart/analytics-series-chart.component';

/**
 * Section analytics **Locations** — **partagée à l'identique** entre les 3
 * dashboards (admin / owner / operator). Les endpoints `rentals/summary` +
 * `rentals/series` répondent à **la même URL** ; le back dérive le périmètre du
 * JWT (admin=plateforme, owner=son parc, operator=ses prestataires). Le front
 * n'envoie donc **aucun** identifiant d'acteur.
 *
 * Garde-fous (mêmes règles que le reste d'analytics) :
 * - **403 = acteur non résolvable** → message dédié ({@link isUnresolvedScope}),
 *   jamais `toErrorMessage`.
 * - `cancellationRate` / `avgPlannedHours` / `lateRate` valent **`null`** (pas
 *   `0`) quand il n'y a pas de base → « — », jamais « 0 % ».
 * - Post-déploiement : funnel historique partiel (backfill ne reconstruit pas
 *   CONFIRMED/STARTED, scope OPERATOR live-only) → « CREATED→(rien)→FINISHED »
 *   est **attendu**. Cf. `docs/ANALYTICS_STATUS.md` §10.
 */
@Component({
  selector: 'app-rentals-analytics',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    TPipe,
    PrettySelectComponent,
    AnalyticsSeriesChartComponent,
    AnalyticsDonutComponent,
  ],
  template: `
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] mb-6">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'analytics.rentals.title' | t }}</h3>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ 'analytics.rentals.subtitle' | t }}</p>
        </div>
        <div class="w-full sm:w-44">
          <app-pretty-select
            [options]="periodOptions"
            [ngModel]="period()"
            (ngModelChange)="changePeriod($event)"
          />
        </div>
      </div>

      @if (loading()) {
        <div class="flex h-[220px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
          {{ 'analytics.loading' | t }}
        </div>
      } @else if (unresolved()) {
        <div class="mt-4 flex items-start gap-3 rounded-xl border border-blue-light-200 bg-blue-light-50 p-4 dark:border-blue-light-500/30 dark:bg-blue-light-500/10">
          <svg class="mt-0.5 shrink-0 text-blue-light-500" width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <p class="text-sm text-blue-light-700 dark:text-blue-light-200">{{ 'analytics.unresolved' | t }}</p>
        </div>
      } @else if (error()) {
        <div class="mt-4 flex items-center justify-between gap-3 rounded-xl border border-error-200 bg-error-50 p-4 dark:border-error-500/30 dark:bg-error-500/10">
          <p class="text-sm text-error-700 dark:text-error-400">{{ error() }}</p>
          <button type="button" (click)="load()" class="shrink-0 text-xs font-medium text-error-700 hover:underline dark:text-error-400">{{ 'common.retry' | t }}</button>
        </div>
      } @else if (summary(); as r) {
        <!-- KPIs -->
        <div class="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.rentals.created' | t }}</span>
            <p class="mt-1 text-xl font-bold text-gray-800 dark:text-white/90">{{ r.createdCount | number }}</p>
          </div>
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.rentals.bookedGmv' | t }}</span>
            <p class="mt-1 text-xl font-bold text-brand-700 dark:text-brand-400">{{ r.bookedGmv | number: '1.0-0' }} <span class="text-xs font-normal text-gray-400">XOF</span></p>
          </div>
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.rentals.cancellationRate' | t }}</span>
            <p class="mt-1 text-xl font-bold text-gray-800 dark:text-white/90">{{ pct(r.cancellationRate) }}</p>
            <p class="mt-0.5 text-xs text-gray-400">{{ 'analytics.rentals.cancelledCount' | t: { count: r.cancelledCount } }}</p>
          </div>
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.rentals.lateRate' | t }}</span>
            <p class="mt-1 text-xl font-bold text-gray-800 dark:text-white/90">{{ pct(r.lateRate) }}</p>
            <p class="mt-0.5 text-xs text-gray-400">{{ 'analytics.rentals.lateDetail' | t: { count: r.lateCount, fees: fmt(r.lateFeeSum) } }}</p>
          </div>
        </div>

        <!-- Funnel compact -->
        <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          <span>{{ 'analytics.rentals.confirmed' | t }} : <strong class="text-gray-700 dark:text-gray-200">{{ r.confirmedCount | number }}</strong></span>
          <span>{{ 'analytics.rentals.started' | t }} : <strong class="text-gray-700 dark:text-gray-200">{{ r.startedCount | number }}</strong></span>
          <span>{{ 'analytics.rentals.finished' | t }} : <strong class="text-gray-700 dark:text-gray-200">{{ r.finishedCount | number }}</strong></span>
          <span>{{ 'analytics.rentals.cancelled' | t }} : <strong class="text-gray-700 dark:text-gray-200">{{ r.cancelledCount | number }}</strong></span>
          <span>{{ 'analytics.rentals.avgDuration' | t }} : <strong class="text-gray-700 dark:text-gray-200">{{ hours(r.avgPlannedHours) }}</strong></span>
        </div>

        <!-- Courbe + donut -->
        <div class="mt-6 grid gap-6 lg:grid-cols-3">
          <div class="lg:col-span-2">
            <h4 class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'analytics.rentals.chartTitle' | t }}</h4>
            <app-analytics-series-chart
              [points]="series()"
              metric="count"
              [seriesName]="seriesName()"
              [emptyLabel]="'analytics.empty' | t"
            />
          </div>
          <div>
            <h4 class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'analytics.rentals.byEventType' | t }}</h4>
            <app-analytics-donut
              [items]="r.byEventType"
              metric="count"
              keyPrefix="analytics.rentals.event"
              [emptyLabel]="'analytics.empty' | t"
            />
          </div>
        </div>
      }
    </section>
  `,
})
export class RentalsAnalyticsComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly period = signal<PeriodPreset>('30d');
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** `true` = acteur non résolvable (403) → message dédié, pas une erreur. */
  readonly unresolved = signal(false);

  readonly summary = signal<RentalSummary | null>(null);
  readonly series = signal<SeriesPoint[]>([]);

  readonly periodOptions: PrettySelectOption<PeriodPreset>[] = [
    { value: '7d', label: 'analytics.period.7d' },
    { value: '30d', label: 'analytics.period.30d' },
    { value: '90d', label: 'analytics.period.90d' },
    { value: '365d', label: 'analytics.period.365d' },
  ];

  ngOnInit(): void {
    this.load();
  }

  changePeriod(p: PeriodPreset | null): void {
    if (!p || p === this.period()) return;
    this.period.set(p);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.unresolved.set(false);
    const period = resolvePeriod(this.period());

    forkJoin({
      summary: this.analytics.rentalSummary(period),
      series: this.analytics.rentalSeries(period),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ summary, series }) => {
          this.summary.set(summary);
          this.series.set(series);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (isUnresolvedScope(err)) {
            this.unresolved.set(true);
          } else {
            this.error.set(toErrorMessage(err, 'errors.analyticsLoadFailed'));
          }
        },
      });
  }

  seriesName(): string {
    return this.locale.t('analytics.rentals.seriesName');
  }

  /** Taux en % — « — » (jamais « 0 % ») quand la base de calcul est absente. */
  pct(v: number | null): string {
    if (v == null) return '—';
    return `${+v.toFixed(1)} %`;
  }

  /** Durée en heures — « — » quand aucune location terminée. */
  hours(v: number | null): string {
    if (v == null) return '—';
    return `${+v.toFixed(1)} h`;
  }

  /** Entier groupé (pour l'interpolation `t()` — le pipe `number` renvoie `string | null`). */
  fmt(n: number): string {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n);
  }
}
